import gql from 'graphql-tag'
import { getProvider } from '@/apollo'
import { splitError } from '@/helper/splitError'
import { CheckoutHelper } from '@/helper/checkoutHelper'

const checkoutHelper = new CheckoutHelper()

const initialState = {
  person: {
    gender: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    password: checkoutHelper.randomPassword(12, '#Aa')
  },
  address: {
    street: '',
    houseNumber: '',
    zip: '',
    city: '',
    supplement: ''
  },
  additional: {
    email: '',
    repeatEmail: '',
    phone: '',
    differentMnpData: false,
    differentFirstName: '',
    differentLastName: '',
    differentBirthDate: '',
    confirmation: false,
    simType: 'SIM_CARD',
    eID: ''
  },
  identification: {
    number: '',
    medium: 'OTHER',
    nationality: '',
    authority: ''
  },
  validationStatus: '',
  backendErrorCode: null,
  backendErrorMessage: null
}

const CheckoutPersonalDataModule = {
  namespaced: true,
  state: initialState,
  getters: {
    person: state => {
      return state.person
    },
    firstName: state => state.person?.firstName,
    lastName: state => state.person?.lastName,
    email: state => state.additional?.email,
    simType: state => state.additional?.simType,
    eID: state => state.additional?.eID,
    contactNumber: state => state.additional?.phone,
    contactNumberFormatted: state => {
      const no = state.additional?.phone || ''
      return no.replace(/([^0-9])/g, '').trim()
    },
    address: state => {
      return state.address
    },
    additional: state => {
      return state.additional
    },
    portability: state => {
      if (state.additional?.differentMnpData) {
        return {
          firstName: state.additional?.differentFirstName,
          lastName: state.additional?.differentLastName,
          birthDate: state.additional?.differentBirthDate
        }
      } else {
        return {
          firstName: state.person?.firstName,
          lastName: state.person?.lastName,
          birthDate: state.person?.birthDate
        }
      }
    },
    identification: state => {
      return state.identification
    },
    validateAddressCore: (state, getters, rootState, rootGetters) => {
      // get tariff from checkout module
      const tariff = rootState.checkout.tariff

      // ensure we use a tariff, try the selected or default if not available
      let useTariff = null
      if (tariff.selectedTariff && tariff.selectedTariff.tariffId) {
        useTariff = tariff.selectedTariff
      } else {
        useTariff = tariff.defaultTariff
      }
      let retailerId = rootGetters.retailerId
      if (useTariff.tariffId === '162' && rootGetters['kwk/kwkOrderByLink']) {
        retailerId = '30861183'
      }

      return {
        firstName: getters.person.firstName,
        lastName: getters.person.lastName,
        gender: getters.person.gender,
        brand: rootGetters.brand,
        market: 'PUC', // enum: [ PUA, PUC, MMO ] - actually PUC for simon
        tariffIdentifier: useTariff.tariffId,
        retailerId: retailerId, // src/flash.js required => Field value.retailerId of required type String! was not provided.
        type: rootGetters.deliveryType // enum [ DELIVERY, BILLING ] // src/flash.js
      }
    },
    validationStatus: state => state.validationStatus,
    backendErrorCode: state => state.backendErrorCode,
    backendErrorMessage: state => state.backendErrorMessage
  },
  actions: {
    async validate ({ dispatch, commit, state, getters, rootGetters }, { person, address, additional, nextStep }) {
      commit('clearMessages', {}, { root: true })
      commit('SET_VALIDATION_STATUS', 'STARTED')
      commit('SET_BACKEND_ERROR_CODE', null)
      commit('SET_BACKEND_ERROR_MESSAGE', null)
      commit('SET_PERSON', person)
      commit('SET_ADDITIONAL', additional)
      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`mutation checkout ($validateAddressInput: ValidateAddressInput!) {
          validateAddress (validateAddressInput: $validateAddressInput){
            address {
              street,
              houseNumber,
              zip,
              city,
              supplement
            }
          }
        }`,
        variables: {
          validateAddressInput: {
            ...getters.validateAddressCore,
            address: address
          }
        }
      }).then((response) => {
        if (response.data.validateAddress.address) {
          // das supplement wird nicht von magam zurückgegeben bei der validierung, taucht aber später wieder auf
          commit('SET_VALIDATION_STATUS', 'SUCCESS')
          commit('SET_ADDRESS', Object.assign({ supplement: address.supplement }, response.data.validateAddress.address))
          commit('checkout/goToStep', nextStep, { root: true })
        } else {
          commit('SET_VALIDATION_STATUS', 'FAILED')
        }
      }).catch((error) => {
        let errorCode = 500
        let errorMessage = 'Es ist ein technischer Fehler aufgetreten.'

        // get error code and message
        if (splitError(error)[0].extensions.magma_code) {
          errorCode = splitError(error)[0].extensions.magma_code
        }
        if (splitError(error)[0].message) {
          errorMessage = splitError(error)[0].message
        }

        // if it's not a technical error, validation failed
        if (errorCode === 500) {
          commit('SET_VALIDATION_STATUS', 'ERROR')
        } else {
          commit('SET_VALIDATION_STATUS', 'FAILED')
        }

        // build error message
        if (errorMessage.indexOf(errorCode) === -1) {
          errorMessage = errorMessage + ' (Fehler: ' + errorCode + ')'
        }

        // update state
        commit('SET_BACKEND_ERROR_CODE', errorCode)
        commit('SET_BACKEND_ERROR_MESSAGE', errorMessage)

        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    },
    resetPersonalDefaultState ({ commit, state }) {
      // initialize
      commit('clearMessages', {}, { root: true })
      commit('resetState', state)
    }
  },
  mutations: {
    SET_PERSON (state, person) {
      state.person = {
        gender: person.gender,
        firstName: person.firstName,
        lastName: person.lastName,
        birthDate: person.birthDate,
        password: person.password
      }
    },
    SET_ADDRESS (state, address) {
      state.address = {
        street: address.street,
        houseNumber: address.houseNumber,
        zip: address.zip,
        city: address.city,
        supplement: address.supplement
      }
    },
    SET_ADDITIONAL (state, additional) {
      state.additional = {
        email: additional.email,
        repeatEmail: additional.repeatEmail,
        simType: additional.simType,
        eID: additional.eID,
        phone: additional.phone,
        differentMnpData: additional.differentMnpData,
        differentFirstName: additional.differentFirstName,
        differentLastName: additional.differentLastName,
        differentBirthDate: additional.differentBirthDate,
        confirmation: additional.confirmation
      }
    },
    SET_SIM_TYPE (state, simType) {
      state.additional.simType = simType
    },
    SET_EID (state, eID) {
      state.additional.eID = eID
    },
    SET_VALIDATION_STATUS (state, status) {
      state.validationStatus = status
    },
    SET_BACKEND_ERROR_CODE (state, code) {
      state.backendErrorCode = code
    },
    SET_BACKEND_ERROR_MESSAGE (state, code) {
      state.backendErrorMessage = code
    },
    resetState (state) {
      Object.assign(state, initialState)
    }
  }
}

export default CheckoutPersonalDataModule
